// 학생 한 명 추가·기본정보 고치기 창 — 학교·이름·보호자·연락처·주소·비고·우유. 주소가 바뀌면 지도 위치를 다시 찾고, 못 찾으면 '확인 필요'로 남긴다
import { useState } from "react";
import { addDoc, collection, deleteField, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { withOrg } from "@/lib/org";
import { normalizePhone } from "@/lib/importValidate";
import { deriveAddressFields, normalizeBungil } from "@/lib/excelParse";
import { geocodeAddress } from "@/lib/vworld";
import { isInRegion } from "@/lib/region";
import { ALL_BRANDS, type Brand, type Student } from "@/types";

import { plainError } from "@/lib/plainError";
interface Props {
  student?: Student; // 없으면 새로 추가
  onClose: (msg?: string) => void;
}

export default function StudentEditor({ student, onClose }: Props) {
  const [f, setF] = useState({
    school: student?.school || "",
    name: student?.name || "",
    guardian: student?.guardian || "",
    phone: student?.phone || "",
    address: student?.rawAddress || student?.refinedAddress || "",
    note: student?.note || "",
    brand: (student?.brand || "서울우유") as Brand,
  });
  // 알림 받지 않음(대표 결정 Q14) — 글 칸이 아니라 따로 둔다
  const [optOut, setOptOut] = useState(student?.notifyOptOut === true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((v) => ({ ...v, [k]: e.target.value }));

  const save = async () => {
    const phone = normalizePhone(f.phone);
    const address = normalizeBungil(f.address.replace(/\s+/g, " ").trim());
    if (!f.school.trim() || !f.name.trim()) return setErr("학교와 이름은 꼭 넣어 주세요.");
    if (!phone && !window.confirm("보호자 연락처가 없습니다. 이 집은 배송 알림 문자를 못 받습니다. 그래도 저장할까요?")) return;
    if (!address) return setErr("배송 주소를 넣어 주세요.");
    setBusy(true);
    setErr("");
    try {
      const addrChanged = !student || address !== (student.rawAddress || student.refinedAddress || "");
      let place: Record<string, unknown> = {};
      let placeMsg = "";
      if (addrChanged) {
        // 지도 위치를 찾는다 — 못 찾으면 지도 위치를 지우고 '확인 필요'로 남겨 경로에서 조용히 틀리지 않게
        const p = await geocodeAddress(address).catch(() => null);
        // 정리 주소는 지도가 돌려준 표준 표기(예: "대구광역시 달서구 월배로 12 (진천동,OO아파트)")로 둔다 —
        // 동 이름·단지명은 이 괄호에서 나오고, 담당 동 화면은 동 이름이 있어야 학생을 보여 준다
        const refined = p && !p.roadMismatch && p.matched ? p.matched : address;
        const fields = deriveAddressFields(refined, address);
        if (p && !p.roadMismatch) {
          place = { lat: p.y, lng: p.x, geocodeStatus: "ok", gu: p.gu || fields.gu, dong: p.dong || fields.dong };
        } else {
          place = { lat: null, lng: null, geocodeStatus: "needs_review", gu: fields.gu, dong: fields.dong };
          placeMsg = p?.roadMismatch
            ? " 다만 지도가 입력과 다른 길로 잡혀 '주소 검토'에서 확인이 필요합니다."
            : " 다만 지도에서 위치를 찾지 못해 '주소 검토'에서 확인이 필요합니다.";
        }
        const gu = (place.gu as string) || "";
        place = {
          ...fields,
          ...place,
          rawAddress: address,
          refinedAddress: refined,
          // 담당 권역 밖(다른 시·도 등)이면 택배로 표시해 기사 경로에 섞이지 않게 한다 — 명단 올리기와 같은 규칙
          ...(student ? {} : isInRegion(refined, gu) ? { courierFlag: false } : { courierFlag: true, courierReason: "always" as const }),
          // 예전 AI 교정 흔적은 새 주소와 무관하니 지운다
          geocodeNeedsCheck: false,
          geocodeReasoning: "",
          geocodeOriginalAddress: "",
          geocodeCandidates: [],
        };
      }
      const base = {
        school: f.school.trim(),
        name: f.name.trim(),
        guardian: f.guardian.trim(),
        phone,
        note: f.note.trim(),
        brand: f.brand,
        ...place,
        // 바꿨을 때만 쓴다 — 보호자가 직접 끈 기록(누가·언제)을 그냥 저장으로 덮지 않게
        ...(optOut !== (student?.notifyOptOut === true)
          ? optOut
            ? { notifyOptOut: true, notifyOptOutBy: "admin" as const, notifyOptOutAt: serverTimestamp() }
            : { notifyOptOut: false, notifyOptOutBy: deleteField(), notifyOptOutAt: deleteField() }
          : {}),
        updatedAt: serverTimestamp(),
      };
      if (student) {
        await updateDoc(doc(db, "students", student.id), base);
        onClose(`✅ ${base.school} ${base.name} 정보를 고쳤습니다.${placeMsg}`);
      } else {
        await addDoc(collection(db, "students"), withOrg({
          ...base,
          sourceGroup: "X",
          postalCode: "",
          company: "자사",
          status: "active",
          boxesPerMonth: 1,
          createdAt: serverTimestamp(),
        }));
        onClose(`✅ ${base.school} ${base.name} 학생을 추가했습니다.${placeMsg}`);
      }
    } catch (e: any) {
      setErr(`저장하지 못했습니다 — ${plainError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const field = "input mt-1 w-full";
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-4" role="dialog" aria-modal="true" aria-label={student ? "학생 정보 고치기" : "학생 추가"}>
      <div className="card w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-5 space-y-3 rounded-b-none sm:rounded-2xl">
        <h2 className="text-lg font-extrabold">{student ? "학생 정보 고치기" : "학생 한 명 추가"}</h2>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm"><span className="font-semibold">학교 *</span><input className={field} value={f.school} onChange={set("school")} placeholder="예: OO초등학교" /></label>
          <label className="block text-sm"><span className="font-semibold">이름 *</span><input className={field} value={f.name} onChange={set("name")} /></label>
          <label className="block text-sm"><span className="font-semibold">보호자</span><input className={field} value={f.guardian} onChange={set("guardian")} /></label>
          <label className="block text-sm"><span className="font-semibold">보호자 연락처</span> <span className="text-xs text-slate-400">없으면 배송 알림이 못 갑니다</span><input className={field} value={f.phone} onChange={set("phone")} inputMode="tel" placeholder="010-0000-0000, 둘이면 쉼표로" /></label>
        </div>
        <label className="flex items-start gap-2 text-sm min-h-[44px]">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={optOut} onChange={(e) => setOptOut(e.target.checked)} />
          <span>
            <span className="font-semibold">알림 받지 않음</span>
            <span className="block text-xs text-slate-500">
              켜면 배송완료 문자를 보내지 않습니다. 배송 기록은 그대로 남습니다.
              {student?.notifyOptOut && student.notifyOptOutBy === "guardian" && " (보호자가 사진 화면에서 직접 껐습니다)"}
            </span>
          </span>
        </label>
        <label className="block text-sm"><span className="font-semibold">배송 주소 *</span><input className={field} value={f.address} onChange={set("address")} placeholder="도로명 주소 + 동·호수" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm"><span className="font-semibold">우유</span>
            <select className={field} value={f.brand} onChange={set("brand")}>{ALL_BRANDS.map((b) => <option key={b}>{b}</option>)}</select>
          </label>
          <label className="block text-sm"><span className="font-semibold">비고</span><input className={field} value={f.note} onChange={set("note")} /></label>
        </div>
        {err && <p className="text-sm text-rose-600" role="alert">{err}</p>}
        <div className="flex gap-2 pt-1">
          <button className="btn-primary flex-1" onClick={save} disabled={busy}>{busy ? "저장 중…" : "저장"}</button>
          <button className="btn-secondary" onClick={() => onClose()} disabled={busy}>닫기</button>
        </div>
      </div>
    </div>
  );
}
